'use client';

import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// ─── Shared types for inline editing ────────────────────────────────────────

export type InlineEdit = { startPage: number; endPage: number };
export type DiffNotifier = Map<number, Set<() => void>>;

// ─── Inline Cell Components (own local state → no parent re-render) ─────────

export function InlinePageInput({
  registrationId,
  field,
  defaultValue,
  min,
  editsRef,
  notifiersRef
}: {
  registrationId: number;
  field: 'startPage' | 'endPage';
  defaultValue: number;
  min: number;
  editsRef: React.RefObject<Map<number, InlineEdit>>;
  notifiersRef: React.RefObject<DiffNotifier>;
}) {
  const [display, setDisplay] = useState(String(defaultValue));

  const commitToRef = (num: number) => {
    const current = editsRef.current!.get(registrationId) ?? {
      startPage: 1,
      endPage: 1
    };
    const updated = { ...current, [field]: num };
    if (field === 'startPage' && updated.endPage < num) {
      updated.endPage = num;
    }
    editsRef.current!.set(registrationId, updated);
    notifiersRef.current!.get(registrationId)?.forEach((cb) => cb());
  };

  return (
    <Input
      type='number'
      min={min}
      value={display}
      onChange={(e) => {
        const raw = e.target.value;
        setDisplay(raw);
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed) && parsed >= min) {
          commitToRef(parsed);
        }
      }}
      onBlur={(e) => {
        const parsed = parseInt(e.target.value, 10);
        const num = isNaN(parsed) || parsed < min ? min : parsed;
        setDisplay(String(num));
        commitToRef(num);
      }}
      onFocus={(e) => e.target.select()}
      className='h-7 w-16 text-center text-xs'
    />
  );
}

export function InlineDiffCell({
  registrationId,
  editsRef,
  notifiersRef,
  sheetLabel
}: {
  registrationId: number;
  editsRef: React.RefObject<Map<number, InlineEdit>>;
  notifiersRef: React.RefObject<DiffNotifier>;
  sheetLabel: string;
}) {
  const getCurrent = () => {
    const edit = editsRef.current!.get(registrationId);
    if (!edit) return 1;
    return Math.max(0, edit.endPage - edit.startPage + 1);
  };

  const [count, setCount] = useState(getCurrent);

  useEffect(() => {
    const notifiers = notifiersRef.current!;
    const cb = () => setCount(getCurrent());
    if (!notifiers.has(registrationId)) {
      notifiers.set(registrationId, new Set());
    }
    notifiers.get(registrationId)!.add(cb);
    return () => {
      notifiers.get(registrationId)?.delete(cb);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationId]);

  if (count <= 0) return null;

  return (
    <Badge
      variant='secondary'
      className='bg-blue-100 text-[10px] whitespace-nowrap text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    >
      {sheetLabel.replace('{count}', String(count))}
    </Badge>
  );
}

export function InlineSaveButton({
  registrationId,
  editsRef,
  isPending,
  onSave,
  label
}: {
  registrationId: number;
  editsRef: React.RefObject<Map<number, InlineEdit>>;
  isPending: boolean;
  onSave: (regId: number, startPage: number, endPage: number) => void;
  label: string;
}) {
  return (
    <Button
      variant='ghost'
      size='icon'
      className='h-7 w-7'
      disabled={isPending}
      onClick={() => {
        const edit = editsRef.current!.get(registrationId) ?? {
          startPage: 1,
          endPage: 1
        };
        if (edit.endPage >= edit.startPage) {
          onSave(registrationId, edit.startPage, edit.endPage);
        }
      }}
      title={label}
    >
      <Save className='h-3.5 w-3.5' />
    </Button>
  );
}
